import React from 'react';
import { Activity, AlertTriangle, Loader2, CheckCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { cn } from '../../lib/utils';
import { useOperations } from '../../contexts/OperationsContext';
import { AnimatedProcessingIndicator } from '../animations/AnimatedOperations';

export const ProcessingIndicator = ({ 
  projectId, 
  collectionId = null,
  size = 'sm', // 'xs', 'sm', 'md'
  showPulse = true,
  className = ''
}) => {
  const { hasActiveOperationsFor, hasFailedOperationsFor } = useOperations();
  
  const hasActive = hasActiveOperationsFor(projectId, collectionId);
  const hasFailed = hasFailedOperationsFor(projectId, collectionId);
  
  // Don't render if no operations
  if (!hasActive && !hasFailed) {
    return null;
  }
  
  const sizeClasses = {
    xs: 'h-2 w-2',
    sm: 'h-2.5 w-2.5', 
    md: 'h-3 w-3'
  };
  
  const iconSize = sizeClasses[size];
  
  if (hasFailed) {
    return (
      <motion.div 
        className={cn('flex items-center', className)} 
        title="Files failed processing"
        initial={{ scale: 0 }}
        animate={{ scale: 1, x: [-1, 1, -1, 0] }}
        transition={{ 
          scale: { type: 'spring', stiffness: 500, damping: 30 },
          x: { duration: 0.4, delay: 0.2 }
        }}
      >
        <AlertTriangle className={cn(iconSize, 'text-red-500')} />
      </motion.div>
    );
  }
  
  if (hasActive) {
    return (
      <AnimatedProcessingIndicator 
        isActive={true}
        className={cn('flex items-center', className)} 
        title="Files processing"
      >
        <Activity 
          className={cn(
            iconSize, 
            'text-blue-500'
          )} 
        />
      </AnimatedProcessingIndicator>
    );
  }
  
  return null;
};

export const DetailedProcessingIndicator = ({ 
  projectId, 
  collectionId = null,
  className = ''
}) => {
  const { filesByProject } = useOperations();
  
  const key = collectionId || projectId;
  const files = filesByProject[key] || [];
  
  if (files.length === 0) return null;
  
  const statusCounts = files.reduce((acc, file) => {
    acc[file.status] = (acc[file.status] || 0) + 1;
    return acc;
  }, {});
  
  const hasActive = (statusCounts.uploading || 0) + (statusCounts.pending || 0) + (statusCounts.processing || 0);
  const hasFailed = statusCounts.failed || 0;
  const hasCompleted = statusCounts.completed || 0;
  
  if (hasActive === 0 && hasFailed === 0) return null;
  
  return (
    <div className={cn('flex items-center gap-1', className)}>
      {hasActive > 0 && (
        <div className="flex items-center gap-1" title={`${hasActive} files processing`}>
          <Loader2 className="h-3 w-3 text-blue-500 animate-spin" />
          <span className="text-xs text-blue-400">{hasActive}</span>
        </div>
      )}
      
      {hasFailed > 0 && (
        <div className="flex items-center gap-1" title={`${hasFailed} files failed`}>
          <AlertTriangle className="h-3 w-3 text-red-500" />
          <span className="text-xs text-red-400">{hasFailed}</span>
        </div>
      )}
      
      {hasCompleted > 0 && hasActive === 0 && hasFailed === 0 && (
        <div className="flex items-center gap-1" title={`${hasCompleted} files completed`}>
          <CheckCircle className="h-3 w-3 text-green-500" />
          <span className="text-xs text-green-400">{hasCompleted}</span>
        </div>
      )}
    </div>
  );
};